import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { take } from 'rxjs';
import { IBrandDetails } from 'src/app/core/domain/brand.model';
import { ConsentService, UnsubscribeScope } from 'src/app/core/service/consent.service';
import { SiteMetadataService } from 'src/app/core/service/site-metadata.service';

type UnsubscribeView = 'loading' | 'confirm' | 'done' | 'invalid';

/**
 * SAN-1479 (DPDP) — one-click unsubscribe from the email footer.
 * `/unsubscribe/:token` carries a signed token. Opening the page changes
 * nothing (mail scanners prefetch links); the user confirms with one click.
 * The old `/account/edit/email/notification/:email` links carried a raw
 * address and are no longer honoured: they show the "link expired" state.
 */
@Component({
  selector: 'app-unsubscribe-page',
  templateUrl: './unsubscribe-page.component.html',
  styleUrls: ['./unsubscribe-page.component.scss']
})
export class UnsubscribePageComponent implements OnInit {
  brandDetails: Partial<IBrandDetails> = {};
  view: UnsubscribeView = 'loading';
  maskedEmail = '';
  scope: UnsubscribeScope = 'marketing';
  submitting = false;
  private token: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private consentService: ConsentService,
    private siteMetadataService: SiteMetadataService,
    private loader: NgxUiLoaderService,
  ) { }

  ngOnInit(): void {
    this.siteMetadataService.setTitle('Unsubscribe from emails');
    this.token = this.route.snapshot.paramMap.get('token');
    if (!this.token) {
      this.view = 'invalid';
      return;
    }

    this.loader.start('isLoader');
    this.consentService.describeUnsubscribeLink(this.token).pipe(take(1)).subscribe({
      next: (res) => {
        this.loader.stop('isLoader');
        this.maskedEmail = res?.email || '';
        this.view = 'confirm';
      },
      error: () => {
        this.loader.stop('isLoader');
        this.view = 'invalid';
      }
    });
  }

  confirmUnsubscribe(): void {
    if (!this.token || this.submitting) return;
    this.submitting = true;
    this.loader.start('isLoader');
    this.consentService.unsubscribe(this.token, this.scope).pipe(take(1)).subscribe({
      next: () => {
        this.loader.stop('isLoader');
        this.submitting = false;
        this.view = 'done';
      },
      error: () => {
        this.loader.stop('isLoader');
        this.submitting = false;
        this.view = 'invalid';
      }
    });
  }
}
